
import { defineComponent } from 'vue'

import { Context } from '@nuxt/types'

import RequestEnterprisePilotLicenseGQLMutation from '~/apollo/mutations/marketing/requestEnterprisePilotLicense.gql'
import {
  EnterprisePilotLicenseSeatChoices,
  EnterprisePilotLicenseVcsChoices,
  RequestEnterprisePilotLicenseInput
} from '~/types/types'

enum FormFields {
  NAME = 'name',
  WORK_EMAIL = 'workEmail',
  JOB_TITLE = 'jobTitle',
  COMPANY_NAME = 'companyName',
  SEATS_COUNT = 'seatsCount',
  VCS_PROVIDER = 'vcsProvider',
  PURPOSE_OF_REQUEST = 'purposeOfRequest'
}

enum FormFieldErrorKeys {
  EMPTY = 'empty',
  VALID = 'valid'
}

type FormFieldErrorT = { empty: boolean | null; valid?: boolean | null }

type FormFieldsDataT = {
  key: FormFields
  label: string
  id: string
  errCondition: boolean | null
  errMsg?: string
  initErrors: Array<FormFieldsErrorT>
}

type FormFieldsErrorT = { key: FormFieldErrorKeys; condition?: boolean; value: boolean | null }

type FormFieldsInfoT = Array<{
  field: FormFields
  errors: Array<FormFieldsErrorT>
}>

export default defineComponent({
  name: 'RequestPilotLicense',
  data() {
    return {
      step: 0,
      fieldRequirementsAreMet: false,
      isFailure: false,
      isSuccess: false,
      isFormSubmitting: false,
      isSubmittingRequest: false,
      name: '',
      confirmName: '',
      workEmail: '',
      jobTitle: '',
      companyName: '',
      seatsCount: '' as EnterprisePilotLicenseSeatChoices,
      vcsProvider: '' as EnterprisePilotLicenseVcsChoices,
      purposeOfRequest: '',
      timerId: null as ReturnType<typeof setTimeout> | null,
      errors: {
        [FormFields.NAME]: { empty: false },
        [FormFields.WORK_EMAIL]: { empty: false, valid: null },
        [FormFields.JOB_TITLE]: { empty: false },
        [FormFields.COMPANY_NAME]: { empty: false },
        [FormFields.SEATS_COUNT]: { empty: false },
        [FormFields.VCS_PROVIDER]: { empty: false },
        [FormFields.PURPOSE_OF_REQUEST]: { empty: false }
      } as Record<FormFields, FormFieldErrorT>,
      seatsCountOptions: [
        { label: 'Below 50', value: EnterprisePilotLicenseSeatChoices.Blw_50 },
        { label: '50-99', value: EnterprisePilotLicenseSeatChoices.Btw_50_99 },
        { label: '100-249', value: EnterprisePilotLicenseSeatChoices.Btw_100_249 },
        { label: '250-499', value: EnterprisePilotLicenseSeatChoices.Btw_250_499 },
        { label: '500-999', value: EnterprisePilotLicenseSeatChoices.Btw_500_999 },
        { label: '1000-2499', value: EnterprisePilotLicenseSeatChoices.Btw_1000_2499 },
        { label: '2500-4999', value: EnterprisePilotLicenseSeatChoices.Btw_2500_4999 },
        { label: '5000+', value: EnterprisePilotLicenseSeatChoices.Abv_5000 }
      ],

      vcsProviders: [
        { label: 'GitHub Cloud (github.com)', value: EnterprisePilotLicenseVcsChoices.GithubCloud },
        {
          label: 'GitHub Enterprise Server',
          value: EnterprisePilotLicenseVcsChoices.GithubEnterpriseServer
        },
        {
          label: 'GitHub Enterprise Cloud',
          value: EnterprisePilotLicenseVcsChoices.GithubEnterpriseCloud
        },
        { label: 'GitLab Cloud (Gitlab.com)', value: EnterprisePilotLicenseVcsChoices.GitlabCloud },
        {
          label: 'GitLab Community Edition',
          value: EnterprisePilotLicenseVcsChoices.GitlabSelfHostedCe
        },
        {
          label: 'GitLab Enterprise Edition',
          value: EnterprisePilotLicenseVcsChoices.GitlabSelfHostedEe
        },
        { label: 'Bitbucket Cloud', value: EnterprisePilotLicenseVcsChoices.BitbucketCloud },
        {
          label: 'Azure DevOps Services',
          value: EnterprisePilotLicenseVcsChoices.AzureDevopsServices
        }
      ]
    }
  },
  async asyncData({ $content }: Context) {
    const content = await $content('pilot-evaluation-agreement').fetch()

    return { content }
  },

  mounted() {
    window.addEventListener('beforeunload', this.confirmBeforeLeave)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.confirmBeforeLeave)
    if (this.timerId) {
      clearTimeout(this.timerId)
    }
  },

  computed: {
    actionDisabled(): boolean {
      return this.confirmName === ''
    },

    title(): string {
      return this.step === 0
        ? 'Request a pilot license for Enterprise Server'
        : 'Pilot evaluation agreement'
    },

    subtitle(): string {
      return this.step === 0
        ? "Please fill in this form to help us understand more about your use-case. After you submit, we'll get back to you with the license."
        : 'Please read the agreement carefully and provide your consent at the bottom.'
    },

    nameField(): FormFieldsDataT {
      return {
        key: FormFields.NAME,
        label: 'Your name',
        id: 'name',
        errCondition: this.errors[FormFields.NAME][FormFieldErrorKeys.EMPTY],
        initErrors: [{ key: FormFieldErrorKeys.EMPTY, value: false }]
      }
    },

    workEmailField(): FormFieldsDataT {
      const errMsg = this.errors[FormFields.WORK_EMAIL][FormFieldErrorKeys.EMPTY]
        ? 'This is a required field.'
        : 'Please enter a valid email.'

      return {
        key: FormFields.WORK_EMAIL,
        label: 'Work email address',
        id: 'work-email',
        errCondition:
          this.errors[FormFields.WORK_EMAIL][FormFieldErrorKeys.EMPTY] ||
          this.errors[FormFields.WORK_EMAIL][FormFieldErrorKeys.VALID] === false,
        errMsg,
        initErrors: [
          { key: FormFieldErrorKeys.EMPTY, value: false },
          { key: FormFieldErrorKeys.VALID, value: null }
        ]
      }
    },

    jobTitleField(): FormFieldsDataT {
      return {
        key: FormFields.JOB_TITLE,
        label: 'Job title',
        id: 'job-title',
        errCondition: this.errors[FormFields.JOB_TITLE][FormFieldErrorKeys.EMPTY],
        initErrors: [{ key: FormFieldErrorKeys.EMPTY, value: false }]
      }
    },

    companyNameField(): FormFieldsDataT {
      return {
        key: FormFields.COMPANY_NAME,
        label: 'Company name',
        id: 'company-name',
        errCondition: this.errors[FormFields.COMPANY_NAME][FormFieldErrorKeys.EMPTY],
        initErrors: [{ key: FormFieldErrorKeys.EMPTY, value: false }]
      }
    },

    seatsCountField(): FormFieldsDataT {
      return {
        key: FormFields.SEATS_COUNT,
        label: 'No. of seats required for the pilot',
        id: 'seats-count',
        errCondition: this.errors[FormFields.SEATS_COUNT][FormFieldErrorKeys.EMPTY],
        initErrors: [{ key: FormFieldErrorKeys.EMPTY, value: false }]
      }
    },

    vcsProviderField(): FormFieldsDataT {
      return {
        key: FormFields.VCS_PROVIDER,
        label: 'Which source code hosting provider do you use?',
        id: 'vcs-provider',
        errCondition: this.errors[FormFields.VCS_PROVIDER][FormFieldErrorKeys.EMPTY],
        initErrors: [{ key: FormFieldErrorKeys.EMPTY, value: false }]
      }
    },

    purposeOfRequestField(): FormFieldsDataT {
      return {
        key: FormFields.PURPOSE_OF_REQUEST,
        label: 'Purpose of requesting the pilot license',
        id: 'purpose-of-request',
        errCondition: this.errors[FormFields.PURPOSE_OF_REQUEST][FormFieldErrorKeys.EMPTY],
        initErrors: [{ key: FormFieldErrorKeys.EMPTY, value: false }]
      }
    },

    formFieldWithValueExists(): boolean {
      const formFieldValues = [
        this.name,
        this.workEmail,
        this.jobTitle,
        this.companyName,
        this.seatsCount,
        this.vcsProvider,
        this.purposeOfRequest
      ]

      return formFieldValues.some(Boolean)
    }
  },
  methods: {
    confirmBeforeLeave(e: Event) {
      // Return early if in the final step
      if (this.step === 2) {
        return
      }

      // Return early if no fields have a value in the first step
      if (this.step === 0 && !this.formFieldWithValueExists) {
        return
      }

      // Cancel the event as stated by the standard
      e.preventDefault()

      // Chrome requires returnValue to be set
      e.returnValue = false
    },

    // Method to set errors triggered on the form field value change event
    resetErrorState(field: FormFields, errors: Array<FormFieldsErrorT>) {
      errors.forEach(({ key, value }) => {
        this.setError(field, key, value)
      })
    },

    setError(fieldKey: FormFields, errorKey: keyof FormFieldErrorT, value: boolean | null) {
      // Fields should have an expected value
      this.fieldRequirementsAreMet =
        errorKey === FormFieldErrorKeys.EMPTY ? value === false : value === null || value === true

      this.errors[fieldKey][errorKey] = value
    },

    validateForm() {
      const emailInput = this.$refs['email-input'] as HTMLInputElement
      emailInput.value = this.workEmail

      const formFieldsInfo: FormFieldsInfoT = [
        {
          field: FormFields.NAME,
          errors: [{ key: FormFieldErrorKeys.EMPTY, condition: !this.name, value: true }]
        },
        {
          field: FormFields.WORK_EMAIL,
          errors: [
            { key: FormFieldErrorKeys.EMPTY, condition: !this.workEmail, value: true },
            {
              key: FormFieldErrorKeys.VALID,
              condition: !emailInput.checkValidity(),
              value: false
            }
          ]
        },
        {
          field: FormFields.JOB_TITLE,
          errors: [{ key: FormFieldErrorKeys.EMPTY, condition: !this.jobTitle, value: true }]
        },
        {
          field: FormFields.COMPANY_NAME,
          errors: [{ key: FormFieldErrorKeys.EMPTY, condition: !this.companyName, value: true }]
        },
        {
          field: FormFields.SEATS_COUNT,
          errors: [{ key: FormFieldErrorKeys.EMPTY, condition: !this.seatsCount, value: true }]
        },
        {
          field: FormFields.VCS_PROVIDER,
          errors: [{ key: FormFieldErrorKeys.EMPTY, condition: !this.vcsProvider, value: true }]
        },
        {
          field: FormFields.PURPOSE_OF_REQUEST,
          errors: [
            { key: FormFieldErrorKeys.EMPTY, condition: !this.purposeOfRequest, value: true }
          ]
        }
      ]

      ;(formFieldsInfo.forEach(({ field, errors }) => {
        errors.forEach(({ key, condition, value }) => {
          if (condition) {
            this.setError(field, key, value)
          }
        })
      }),
        (emailInput.value = ''))

      if (this.fieldRequirementsAreMet) {
        this.isFormSubmitting = true

        this.timerId = setTimeout(() => {
          this.step++

          this.isFormSubmitting = false
        }, 1000)
      }
    },

    async sendRequest() {
      if (this.actionDisabled) {
        return
      }

      this.isSubmittingRequest = true

      try {
        const input: RequestEnterprisePilotLicenseInput = {
          name: this.name,
          email: this.workEmail,
          jobTitle: this.jobTitle,
          companyName: this.companyName,
          seats: this.seatsCount,
          vcs: this.vcsProvider,
          purpose: this.purposeOfRequest
        }
        await this.$applyGraphqlMutation(RequestEnterprisePilotLicenseGQLMutation, { input })

        this.isSuccess = true
      } catch (err) {
        this.isFailure = true
      } finally {
        this.step++
        this.isSubmittingRequest = false
      }
    }
  }
})
